import { useState } from 'react';
import {
  TablePagination,
  Box,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableHead,
} from '@mui/material';
import {
  DialogContainer,
  DialogBody,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../../common/components';
import { useExcelExport } from '../../hooks/useExcelExport';
import { usePDFExport } from '../../hooks/usePDFExport';
import { PAGINATION_CONFIG } from '../../constants/statisticsConstants';
import StatisticsDialogTitle from '../common/StatisticsDialogTitle';

const IssuedCouponsModal = ({ open, handleClose, header, results }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE);

  const { exportToExcel } = useExcelExport();
  const { exportToPDF } = usePDFExport();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = () => {
    const data = results.map((result) => ({
      'Número de Contrato': result.contract_number,
      Suscriptor: result.subscriber_name,
      'Cuotas del Plan': result.installments_number,
      'Cuotas Emitidas': result.generated_fees.toFixed(2),
      'Cuotas a Terminar': result.remaining_fees.toFixed(2),
    }));

    exportToExcel(data, 'CuponesEmitidos');
  };

  const handleExportToPDF = () => {
    const pdfData = {
      title: 'Reporte de Cupones Emitidos',
      headers: ['N° Contrato', 'Suscriptor', 'Cuotas Plan', 'Emitidas', 'A Terminar'],
      data: results.map((result) => [
        result.contract_number.toString(),
        result.subscriber_name,
        result.installments_number.toString(),
        result.generated_fees.toFixed(2).toString(),
        result.remaining_fees.toFixed(2).toString(),
      ]),
      filename: 'CuponesEmitidos',
    };

    exportToPDF(pdfData);
  };

  if (!header || !results || results.length === 0) {
    return null;
  }

  const paginatedResults = results.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getRowStyles = (remainingFees) => {
    if (remainingFees <= 10) {
      return {
        backgroundColor: '#fdecea',
        '&:hover': { backgroundColor: '#f9d7d3' },
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          border: '2px solid #d32f2f',
          pointerEvents: 'none',
        },
      };
    }
    return undefined;
  };

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <StatisticsDialogTitle
        title="Cupones Emitidos"
        onExportExcel={handleExportToExcel}
        onExportPDF={handleExportToPDF}
        onClose={handleClose}
      />
      <DialogBody>
        <Box
          sx={{ mb: 3, textAlign: 'center', border: '1px solid #4a90e2', p: 2, borderRadius: 2 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333', mb: 1 }}>
            {header.constructora_info}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic', mb: 0.5 }}>
            Actualizado a la fecha: {header.updated_at_date}
          </Typography>
          <Typography variant="body2" sx={{ color: '#555' }}>
            {header.date_filter_text}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem', color: '#555' }}>
          Cantidad de contratos encontrados: {results.length}
        </Typography>

        <GeneralTableBox>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <HeadTableCell>N° de Contrato</HeadTableCell>
                <HeadTableCell>Suscriptor</HeadTableCell>
                <HeadTableCell>Cuotas del Plan</HeadTableCell>
                <HeadTableCell>Cuotas Emitidas</HeadTableCell>
                <HeadTableCell>Cuotas a Terminar</HeadTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedResults.map((result, index) => (
                <TableRow key={index} sx={getRowStyles(result.remaining_fees)}>
                  <RowTableCell>{result.contract_number}</RowTableCell>
                  <RowTableCell>{result.subscriber_name}</RowTableCell>
                  <RowTableCell>{result.installments_number}</RowTableCell>
                  <RowTableCell>{result.generated_fees.toFixed(2)}</RowTableCell>
                  <RowTableCell>{result.remaining_fees.toFixed(2)}</RowTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </GeneralTableBox>

        <TablePagination
          component="div"
          count={results.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Contratos por página"
          rowsPerPageOptions={PAGINATION_CONFIG.ROWS_PER_PAGE_OPTIONS}
        />
      </DialogBody>
    </DialogContainer>
  );
};

export default IssuedCouponsModal;
